import { useEffect } from 'react';
/**
 * WEB-1099 — where a toast stack lands under pinned surface chrome.
 *
 * Design ruling (DQ-10): toasts land 12px under the pinned chrome — the header
 * plus any sticky surface bar under it. A surface opts its pinned bar in with
 * `data-ln-chrome`. When a toast opens an EMPTY stack, the region measures once:
 *
 *   top = max(lowest visible `[data-ln-chrome]` bottom + 12, default top)
 *
 * where the default top is the region's own computed `top` with no override
 * (the app's `--toast-inset-top`: published chrome height + safe area + 12).
 * The result is written to the region as `--toast-stack-top` and held until
 * the stack empties. It never tracks scroll, and a second toast joins the open
 * stack at its current top. The host stylesheet decides where the property
 * applies (`apps/web/styles/surfaces/toast.css`: ≤720 only).
 *
 * Modal overlays (the filter sheet, the map takeover) do not carry
 * `data-ln-chrome`, so they never push the stack down.
 */
/** Custom property the region carries while its stack is open. */
export const TOAST_STACK_TOP_VAR = '--toast-stack-top';
/** Attribute a pinned surface bar sets to push the toast stack below it. */
export const TOAST_CHROME_ATTR = 'data-ln-chrome';
/** Gap between the lowest pinned chrome edge and the first toast. */
export const TOAST_CHROME_GAP_PX = 12;
/**
 * The top the stack should open at, or `null` when no pinned chrome is
 * visible and the region's default top already applies.
 */
export function measureToastStackTop(region) {
    const doc = region.ownerDocument;
    const view = doc.defaultView;
    if (!view)
        return null;
    let lowestChromeBottom = null;
    for (const el of Array.from(doc.querySelectorAll(`[${TOAST_CHROME_ATTR}]`))) {
        const rect = el.getBoundingClientRect();
        // Hidden or scrolled-away chrome does not pin anything.
        if (rect.height <= 0 || rect.bottom <= 0)
            continue;
        if (lowestChromeBottom === null || rect.bottom > lowestChromeBottom) {
            lowestChromeBottom = rect.bottom;
        }
    }
    if (lowestChromeBottom === null)
        return null;
    // Read the default with any override lifted, then put the override back
    // exactly as it was: measuring must never move an open stack.
    const held = region.style.getPropertyValue(TOAST_STACK_TOP_VAR);
    const heldPriority = region.style.getPropertyPriority(TOAST_STACK_TOP_VAR);
    let defaultTop;
    try {
        region.style.removeProperty(TOAST_STACK_TOP_VAR);
        defaultTop = Number.parseFloat(view.getComputedStyle(region).top);
    }
    finally {
        if (held !== '')
            region.style.setProperty(TOAST_STACK_TOP_VAR, held, heldPriority);
    }
    const chromeTop = lowestChromeBottom + TOAST_CHROME_GAP_PX;
    return Number.isFinite(defaultTop) ? Math.max(chromeTop, defaultTop) : chromeTop;
}
/**
 * Applies the measured top to `region` whenever its stack goes from empty to
 * non-empty, and clears it when the stack empties again. Children added by
 * any tenant (the Redux queue, a portalled page-scoped toast) count.
 */
export function holdToastStackTop(region) {
    let open = region.childElementCount > 0;
    const apply = () => {
        const top = measureToastStackTop(region);
        if (top === null) {
            region.style.removeProperty(TOAST_STACK_TOP_VAR);
        }
        else {
            region.style.setProperty(TOAST_STACK_TOP_VAR, `${Math.round(top)}px`);
        }
    };
    if (open)
        apply();
    const View = region.ownerDocument.defaultView;
    if (!View || typeof View.MutationObserver === 'undefined')
        return () => undefined;
    const observer = new View.MutationObserver(() => {
        const nowOpen = region.childElementCount > 0;
        if (nowOpen && !open)
            apply();
        if (!nowOpen && open)
            region.style.removeProperty(TOAST_STACK_TOP_VAR);
        open = nowOpen;
    });
    observer.observe(region, { childList: true });
    return () => {
        observer.disconnect();
        region.style.removeProperty(TOAST_STACK_TOP_VAR);
    };
}
/** React binding for {@link holdToastStackTop}. Use it on the region owner only. */
export function useToastStackTop(regionRef) {
    useEffect(() => {
        const region = regionRef.current;
        if (!region)
            return;
        return holdToastStackTop(region);
    }, [regionRef]);
}
